/**
 * Turn a `paperclip …` (or raw shell) command into a short, human-readable
 * description of what the agent is doing — "Searching PubMed Central",
 * "Reading a paper", "Saving a file to your clipboard" — instead of showing
 * the raw command as the primary label.
 *
 * `summarizeShellCommand` returns `null` when the command isn't one we have a
 * nicer phrasing for, so callers fall back to the raw command. The raw command
 * is still available on click-to-expand.
 *
 * Coverage is intentionally broad: it spans the Paperclip CLI commands, the
 * virtual-filesystem verbs (ls/cat/grep/…), the clipboard verbs, and the
 * search/analysis pipeline (search/map/reduce/…). See the command inventory in
 * `apps/tools/src/modules/virtual_filesystem/terminal.py` and
 * `apps/backend/src/routes/cli.py`.
 */

/** Friendly names for `-s/--source` values (and their aliases). */
const SOURCE_LABELS: Record<string, string> = {
  papers: "papers",
  pmc: "PubMed Central",
  biorxiv: "bioRxiv",
  medrxiv: "medRxiv",
  arxiv: "arXiv",
  abstracts: "abstracts",
  nature_reviews: "peer reviews",
  openreview: "peer reviews",
  peer_reviews: "peer reviews",
  fda: "FDA documents",
  "fda/us": "FDA documents",
  "fda/jp": "Japanese regulatory documents",
  "fda/eu": "European regulatory documents",
  trials: "clinical trials",
  "trials/us": "clinical trials",
  "trials/eu": "clinical trials",
  "trials/jp": "clinical trials",
  "trials/cn": "clinical trials",
  "trials/intl": "clinical trials",
  patents: "patents",
  proteins: "protein databases",
  uniprot: "protein databases",
  pdb: "protein databases",
  chembl: "protein databases",
  clipboard: "your clipboard",
  sec: "SEC filings",
  conferences: "conference abstracts",
};

/** The document "domain" a virtual-filesystem path points at. */
const pathDomain = (path: string): string => {
  const p = path.replace(/^["']|["']$/g, "").toLowerCase();
  if (p.startsWith("/clipboard")) return "your clipboard";
  if (p.startsWith("/papers")) return "a paper";
  if (p.startsWith("/peer_reviews")) return "a peer review";
  if (p.startsWith("/fda")) return "an FDA document";
  if (p.startsWith("/trials") || p.startsWith("/clinicaltrials"))
    return "a clinical trial";
  if (p.startsWith("/patents")) return "a patent";
  if (p.startsWith("/proteins")) return "a protein entry";
  if (p.startsWith("/library")) return "your library";
  if (p.startsWith("/repos")) return "a repo";
  return "a document";
};

const PAPER_SOURCES = new Set(["papers", "pmc", "biorxiv", "medrxiv", "arxiv"]);

const sourceLabel = (raw: string): string => {
  const keys = raw.toLowerCase().split(",").filter(Boolean);
  if (keys.length > 1) {
    // `-s pmc,biorxiv` → treat a mixed paper list as just "papers".
    return keys.every((key) => PAPER_SOURCES.has(key)) ? "papers" : "several sources";
  }
  return SOURCE_LABELS[keys[0] ?? ""] ?? "the corpus";
};

const firstQuoted = (cmd: string): string | undefined =>
  cmd.match(/"([^"]+)"/)?.[1] ?? cmd.match(/'([^']+)'/)?.[1];

const firstPath = (cmd: string): string | undefined =>
  // A VFS path argument: /papers/…, /clipboard/…, etc. (skip flags).
  cmd.match(/(?:^|\s)(\/[A-Za-z0-9._\-/]+)/)?.[1];

const sourceFlag = (cmd: string): string | undefined =>
  cmd.match(/(?:^|\s)(?:-s|--source)[\s=]+(\S+)/)?.[1];

/**
 * The first command of a pipeline and its shell words, without quotes. A `|`
 * in quotes, such as the regex of `grep "a|b"`, is part of a word.
 */
const firstCommand = (cmd: string): { text: string; words: string[] } => {
  const words: string[] = [];
  let word = "";
  let inWord = false;
  let quote = "";
  for (let i = 0; i < cmd.length; i++) {
    const ch = cmd[i];
    if (quote) {
      if (ch === quote) quote = "";
      else if (ch === "\\" && quote === '"' && /["\\]/.test(cmd[i + 1] ?? "")) {
        word += cmd[++i];
      } else word += ch;
    } else if (ch === "|") {
      if (inWord) words.push(word);
      return { text: cmd.slice(0, i), words };
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      inWord = true;
    } else if (ch === "\\" && i + 1 < cmd.length) {
      word += cmd[++i];
      inWord = true;
    } else if (/\s/.test(ch)) {
      if (inWord) words.push(word);
      word = "";
      inWord = false;
    } else {
      word += ch;
      inWord = true;
    }
  }
  if (inWord) words.push(word);
  return { text: cmd, words };
};

/** grep options that take a value; the value is never the search pattern. */
const GREP_VALUE_OPTIONS = new Set([
  "-m",
  "-e",
  "-A",
  "-B",
  "-C",
  "--from",
  "--block-type",
  "--section",
  "--source",
]);

const FIGURE_BLOCK_TYPES = new Set(["figure", "image", "graphic"]);

/** The grep pattern: the first word that is not an option, its value, or a path. */
const grepPattern = (words: string[]): string | undefined => {
  let extraPattern: string | undefined;
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if (GREP_VALUE_OPTIONS.has(word)) {
      if (word === "-e") extraPattern ??= words[i + 1];
      i++;
    } else if (word && !word.startsWith("-") && !word.startsWith("/")) {
      return word;
    }
  }
  return extraPattern;
};

const searchesOnlyFigures = (words: string[]): boolean => {
  const index = words.indexOf("--block-type");
  const value =
    index >= 0
      ? words[index + 1]
      : words.find((w) => w.startsWith("--block-type="))?.slice("--block-type=".length);
  const types = (value ?? "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  return types.length > 0 && types.every((t) => FIGURE_BLOCK_TYPES.has(t));
};

export const summarizeShellCommand = (rawCmd: string): string | null => {
  let cmd = (rawCmd ?? "").trim();
  if (!cmd) return null;
  if (cmd.startsWith("paperclip ")) cmd = cmd.slice("paperclip ".length).trim();
  // Focus on the leading (primary) action when the agent pipes output onward.
  const first = firstCommand(cmd);
  cmd = first.text.trim();
  if (!cmd) return null;

  const verb = cmd.split(/\s+/)[0].toLowerCase();
  const quoted = firstQuoted(cmd);
  const src = sourceFlag(cmd);
  const path = firstPath(cmd);
  const q = quoted ? `: “${quoted}”` : "";

  switch (verb) {
    // ── Discovery & search ──
    case "search": {
      const where = src ? sourceLabel(src) : path ? pathDomain(path) : null;
      return where ? `Searching ${where}${q}` : `Searching${q || " the corpus"}`;
    }
    case "searches":
      return "Running several searches";
    case "grep":
    case "egrep":
    case "fgrep": {
      const pat = grepPattern(first.words);
      const scope = path ? ` in ${pathDomain(path)}` : "";
      if (searchesOnlyFigures(first.words)) {
        return `Searching figure captions${pat ? ` for “${pat}”` : ""}${scope}`;
      }
      return pat ? `Searching for “${pat}”${scope}` : `Searching the text${scope}`;
    }
    case "scan":
      return `Scanning ${path ? pathDomain(path) : "a document"} for patterns`;
    case "lookup":
      return "Looking up a document by metadata";
    case "lookup-citation":
      return "Finding in-text citations";
    case "sql":
      return "Querying metadata";
    case "filter":
      return "Filtering results by relevance";
    case "vec":
      return "Comparing documents by meaning";
    case "links":
    case "links-search":
    case "links-browse":
    case "links-stats":
      return "Cross-referencing databases";
    case "funded-by":
      return "Finding papers by funder";
    case "oa":
      return "Finding open-access full text";
    case "papers_that_cite":
      return "Finding papers that cite this one";
    case "websearch":
      return "Searching the web";
    case "curl":
      return "Fetching a web page";

    // ── Read & analyze ──
    case "cat":
    case "less":
    case "more":
      return `Reading ${path ? pathDomain(path) : "a document"}`;
    case "head":
      return `Skimming ${path ? pathDomain(path) : "a document"}`;
    case "tail":
      return `Reading the end of ${path ? pathDomain(path) : "a document"}`;
    case "ls":
      return path ? `Browsing ${pathDomain(path)}` : "Listing files";
    case "tree":
      return "Mapping the folder tree";
    case "wc":
      return "Counting lines";
    case "ask_image":
    case "ask-image":
      return "Reading a figure";
    case "map":
      return "Reading across papers";
    case "reduce":
      return "Synthesizing the findings";
    case "verify":
      return "Verifying claims against sources";
    case "export":
      return "Exporting results";
    case "results":
      return "Reviewing saved results";

    // ── Clipboard & files ──
    case "cp": {
      const linking = path && !/^\/clipboard/i.test(path);
      return linking
        ? "Linking a document to your clipboard"
        : "Copying into your clipboard";
    }
    case "mv":
      return "Moving files in your clipboard";
    case "rm":
      return "Removing from your clipboard";
    case "mkdir":
      return "Creating a clipboard folder";
    case "rmdir":
      return "Removing a clipboard folder";
    case "find":
      return "Finding files in your clipboard";
    case "du":
      return "Checking clipboard storage";
    case "stats":
      return "Checking clipboard stats";
    case "reindex":
      return "Re-indexing your clipboard";
    case "upload":
      return "Saving a file to your clipboard";
    case "import":
      return "Importing references";
    case "fetch":
      return "Fetching a paper";
    case "sync":
      return "Syncing local PDFs";
    case "share":
      return "Sharing a folder";
    case "unshare":
      return "Removing shared access";
    case "library":
      return "Opening your library";

    // ── Repos ──
    case "repo":
    case "repos":
    case "git":
      return "Working with a paper repo";

    // ── Meta / local ──
    case "citation-explorer":
      return "Exploring citations";
    case "agent":
      return "Running an agent";
    case "skill":
      return "Loading documentation";
    case "skills":
      return "Using a deprecated Paperclip command";
    case "routines":
      return "Running a Paperclip routine";
    case "config":
      return "Checking settings";
    case "login":
      return "Signing in";
    case "logout":
      return "Signing out";
    case "setup":
      return "Setting up Paperclip";
    case "update":
      return "Updating Paperclip";
    case "install":
      return "Installing the skill";
    case "uninstall":
      return "Uninstalling Paperclip";
    case "python":
    case "python3":
      return "Running analysis code";

    default:
      // Plumbing (echo/sed/awk/jq/sort/…) and anything unmapped: let the caller
      // fall back to showing the raw command.
      return null;
  }
};
